import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Link2 } from 'lucide-react';

// Property Settings → Link Room Types
// Lets an admin pick a base room type, link dependent room types and set an
// increment (fixed ₹ or %) per rate plan × occupancy. Persisted to
// /properties/{id}/room-type-links.
export const LinkRoomTypesSection = ({ apiBase, propertyId, roomsData, links, onSaved }) => {
  // roomsData: [{ room_type_id, room_type_name, max_occupancy, rate_plans:[{id, code, name}] }]
  const [baseId, setBaseId] = useState('');
  // draft: { [linkedRoomTypeId]: { increment_type, increments: {code:{occ:value}} } }
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const roomById = useMemo(() => {
    const m = {};
    (roomsData || []).forEach(r => { m[r.room_type_id] = r; });
    return m;
  }, [roomsData]);

  const baseRoom = baseId ? roomById[baseId] : null;
  const basePlans = baseRoom?.rate_plans || [];
  const baseMaxOcc = baseRoom?.max_occupancy || 1;
  const baseOccs = Array.from({ length: baseMaxOcc }, (_, i) => i + 1);

  const existingForBase = useMemo(
    () => (links || []).find(l => l.base_room_type_id === baseId),
    [links, baseId]
  );

  // Load existing config into the draft whenever the base changes.
  useEffect(() => {
    if (!baseId) { setDraft({}); return; }
    const next = {};
    (existingForBase?.links || []).forEach(l => {
      next[l.linked_room_type_id] = {
        increment_type: l.increment_type || 'fixed',
        increments: JSON.parse(JSON.stringify(l.increments || {})),
      };
    });
    setDraft(next);
  }, [baseId, existingForBase]);

  const toggleLinked = (rtId, checked) => {
    setDraft(prev => {
      const next = { ...prev };
      if (checked) {
        next[rtId] = next[rtId] || { increment_type: 'fixed', increments: {} };
      } else {
        delete next[rtId];
      }
      return next;
    });
  };

  const setIncType = (rtId, type) => {
    setDraft(prev => ({ ...prev, [rtId]: { ...prev[rtId], increment_type: type } }));
  };

  const setIncValue = (rtId, code, occ, value) => {
    setDraft(prev => {
      const entry = prev[rtId] || { increment_type: 'fixed', increments: {} };
      const inc = { ...(entry.increments || {}) };
      const byOcc = { ...(inc[code] || {}) };
      if (value === '' || value === null || value === undefined) {
        delete byOcc[String(occ)];
      } else {
        byOcc[String(occ)] = parseFloat(value);
      }
      if (Object.keys(byOcc).length === 0) delete inc[code]; else inc[code] = byOcc;
      return { ...prev, [rtId]: { ...entry, increments: inc } };
    });
  };

  const save = async () => {
    if (!baseId) { toast.error('Select a base room type first'); return; }
    const linksPayload = Object.entries(draft).map(([linked_room_type_id, cfg]) => ({
      linked_room_type_id,
      increment_type: cfg.increment_type || 'fixed',
      increments: cfg.increments || {},
    }));
    try {
      setSaving(true);
      await axios.put(`${apiBase}/properties/${propertyId}/room-type-links`, {
        base_room_type_id: baseId,
        links: linksPayload,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success(linksPayload.length ? 'Room type links saved' : 'Room type links cleared');
      if (onSaved) await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save room type links');
    } finally {
      setSaving(false);
    }
  };

  const linkableRooms = (roomsData || []).filter(r => r.room_type_id !== baseId);

  return (
    <Card data-testid="link-room-types-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Link2 className="w-4 h-4 text-orange-600" /> Link Room Types
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-gray-600">
          Link dependent room types to a base room type. When you update a rate for the base in the
          Calendar, you can choose to also update its linked room types — each linked rate becomes the
          base's new rate <strong>plus the increment</strong> you set below (per rate plan &amp; occupancy).
        </div>

        {(roomsData || []).length < 2 ? (
          <div className="p-3 text-sm text-gray-500 bg-gray-50 rounded">
            Add at least two room types to this property to configure links.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Base room type</label>
              <Select value={baseId} onValueChange={setBaseId}>
                <SelectTrigger className="w-64" data-testid="link-base-room-select">
                  <SelectValue placeholder="Select a base room type" />
                </SelectTrigger>
                <SelectContent>
                  {(roomsData || []).map(r => (
                    <SelectItem key={r.room_type_id} value={r.room_type_id} data-testid={`link-base-option-${r.room_type_id}`}>
                      {r.room_type_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {baseId && (
              <div className="space-y-4">
                <div className="text-xs text-gray-500">
                  Select the room types to link to <strong>{baseRoom?.room_type_name}</strong>, then set increments.
                </div>
                {linkableRooms.map(lr => {
                  const checked = !!draft[lr.room_type_id];
                  const cfg = draft[lr.room_type_id] || { increment_type: 'fixed', increments: {} };
                  return (
                    <div key={lr.room_type_id} className="border rounded-lg p-3 bg-white" data-testid={`link-linked-row-${lr.room_type_id}`}>
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => toggleLinked(lr.room_type_id, !!v)}
                            data-testid={`link-linked-checkbox-${lr.room_type_id}`}
                          />
                          <span className="text-sm font-semibold text-gray-800">{lr.room_type_name}</span>
                        </label>
                        {checked && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Increment type</span>
                            <Select value={cfg.increment_type} onValueChange={(v) => setIncType(lr.room_type_id, v)}>
                              <SelectTrigger className="w-32 h-8" data-testid={`link-inc-type-${lr.room_type_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="fixed">Fixed (₹)</SelectItem>
                                <SelectItem value="percentage">Percentage (%)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>

                      {checked && (
                        <div className="mt-3 overflow-x-auto">
                          {basePlans.length === 0 ? (
                            <div className="text-xs text-gray-500 italic">Base room type has no rate plans configured.</div>
                          ) : (
                            <table className="text-xs border-collapse">
                              <thead>
                                <tr>
                                  <th className="px-2 py-1 text-left text-gray-500 font-medium">Rate Plan</th>
                                  {baseOccs.map(o => (
                                    <th key={o} className="px-2 py-1 text-center text-gray-500 font-medium">Occ {o}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {basePlans.map(rp => (
                                  <tr key={rp.id} className="border-t">
                                    <td className="px-2 py-1 font-medium text-gray-700 whitespace-nowrap">
                                      {rp.code}{rp.name ? ` — ${rp.name}` : ''}
                                    </td>
                                    {baseOccs.map(o => (
                                      <td key={o} className="px-1 py-1">
                                        <div className="flex items-center gap-1">
                                          <span className="text-gray-400">{cfg.increment_type === 'percentage' ? '%' : '+'}</span>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            className="w-20 h-7 text-xs"
                                            value={cfg.increments?.[rp.code]?.[String(o)] ?? ''}
                                            onChange={(e) => setIncValue(lr.room_type_id, rp.code, o, e.target.value)}
                                            data-testid={`link-inc-${lr.room_type_id}-${rp.code}-${o}`}
                                            placeholder="0"
                                          />
                                        </div>
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="bg-gradient-to-r from-orange-500 to-amber-600 text-white"
                    data-testid="save-room-type-links-btn"
                  >
                    {saving ? 'Saving…' : 'Save Links'}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LinkRoomTypesSection;
